import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { inputCls } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={user.role === "partner" ? "/portal" : "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(u.role === "partner" ? "/portal" : "/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="absolute bottom-0 -left-24 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <LogoMark className="w-11 h-11" />
          <div>
            <div className="text-white font-bold font-heading text-lg">Arjun Fintech LLP</div>
            <div className="text-xs uppercase tracking-widest text-emerald-400 font-mono">DSA Platform</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-extrabold text-white font-heading leading-tight tracking-tight">
            Loan sourcing,<br /><span className="text-emerald-400">simplified.</span>
          </h1>
          <p className="mt-5 text-slate-400 text-base max-w-md leading-relaxed">
            Manage partners, track every loan from lead to disbursement, and settle commissions — all in one command center.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["Partner Hierarchy", "Loan Pipeline", "Auto Commission", "WhatsApp Alerts"].map((t) => (
              <span key={t} className="px-3 py-1.5 rounded-full bg-slate-800 text-slate-300 text-xs font-medium border border-slate-700">{t}</span>
            ))}
          </div>
        </div>
        <div className="text-slate-600 text-xs font-mono relative">© 2026 Arjun Fintech LLP</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <LogoMark className="w-10 h-10" />
            <div className="text-slate-900 font-bold font-heading text-lg">Arjun Fintech</div>
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 font-heading">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-8">Access your DSA operations dashboard</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required className={inputCls} placeholder="you@arjunfintech.com" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Password</label>
              <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required className={inputCls} placeholder="••••••••" />
            </div>
            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-emerald-600 hover:text-emerald-700">Forgot password?</Link>
            </div>
            {error && <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm transition-all shadow-sm active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>Sign in <ArrowRight size={16} /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
